"use client";

import { useSelection } from "@trydig/centra/client";
import type { PaymentResult } from "@trydig/centra-types/orders";
import { useCallback, useEffect, useRef } from "react";
import { getUserData, setUserData } from "@/features/global/user_data";
import { usePathname } from "@/i18n/routing";
import { gtm } from "@/lib/gtm";
import type { ProductCard, ProductDetails } from "../centra";
import { getBaseUrl } from "../utils";
import {
	productCardsToGTMProducts,
	productDetailsToGTMProduct,
	selectionItemsToGTMProducts,
	sendGTMEvent,
} from "./utils";

export const ViewItem = ({ product }: { product: ProductDetails }) => {
	const hasRan = useRef<boolean>(false);
	const { data: selection } = useSelection();

	useEffect(() => {
		if (!selection?.selection?.currency || hasRan.current) return;

		const item = productDetailsToGTMProduct({
			product,
			price: {
				discountPercent: 0,
				price: product.price,
				priceAsNumber: product.priceAsNumber,
				priceBeforeDiscount: product.priceBeforeDiscount,
				priceBeforeDiscountAsNumber: product.priceBeforeDiscountAsNumber,
			},
			currency: selection.selection.currency,
		});

		gtm.viewItem(item);

		hasRan.current = true;
	}, [product, selection?.selection?.currency]);

	return null;
};

export const ViewItemList = ({
	products,
	categoryPage,
	categoryId,
}: {
	products: ProductCard[];
	categoryPage: string;
	categoryId: string;
}) => {
	const hasRan = useRef<boolean>(false);
	const { data: selection } = useSelection();

	useEffect(() => {
		if (!products || !selection?.selection?.currency) return;

		const items = productCardsToGTMProducts({
			products,
			currency: selection.selection.currency,
			categoryPage,
		});

		gtm.viewItemList({
			items,
			currency: selection.selection.currency,
			item_list_id: categoryId,
			item_list_name: categoryPage,
		});

		hasRan.current = true;
	}, [categoryId, categoryPage, products, selection?.selection?.currency]);
	return null;
};

export const SelectShippingMethodEvent = () => {
	const { data: selection } = useSelection();

	useEffect(() => {
		if (
			!selection?.selection?.shippingMethodName ||
			!selection.selection.currency ||
			!selection?.selection?.items
		)
			return;

		const items = selectionItemsToGTMProducts({
			items: selection.selection.items,
			currency: selection.selection.currency,
		});
		gtm.addShippingMethod({
			shipping_tier: selection.selection.shippingMethodName,
			currency: selection.selection.currency,
			items,
			value: items?.reduce((a, b) => (a + b.price) * (b.quantity || 1), 0),
		});
	}, [selection]);

	return null;
};

export const BeginCheckoutEvent = () => {
	const { data: selection } = useSelection();

	useEffect(() => {
		if (
			!selection?.selection?.items ||
			selection?.selection?.items?.length === 0 ||
			!selection?.selection?.currency
		)
			return;

		const items = selectionItemsToGTMProducts({
			items: selection.selection.items,
			currency: selection.selection.currency,
		});

		if (items.length > 0) {
			gtm.beginCheckout({
				items,
				currency: selection.selection.currency,
				value: items?.reduce((a, b) => (a + b.price) * (b.quantity || 1), 0),
			});
		}
	}, [selection?.selection?.items, selection?.selection?.currency]);

	return null;
};

export const NewlsetterSubscribeEvent = () => {
	useEffect(() => {
		const controller = new AbortController();

		const handleKlaviyoSubmit = async (e: Event) => {
			const event = e as Partial<{
				detail: {
					type: string;
					metaData: {
						$email: string;
						email: string;
					};
				};
			}>;

			if (event.detail?.type !== "submit") return;

			const email =
				event.detail?.metaData?.$email || event.detail?.metaData?.email || null;

			if (!email) return;

			const userdata = await setUserData({
				user_data: {
					email: email,
				},
			});

			sendGTMEvent({
				event: "newsletter",
				ecommerce: null,
				user_data: userdata,
			});
		};

		window.addEventListener("klaviyoForms", handleKlaviyoSubmit, {
			signal: controller.signal,
		});

		return () => {
			controller.abort();
		};
	}, []);

	return null;
};

export const PageView = () => {
	const { data: location } = useSelection({
		select: (data) => data.location,
	});
	const pathname = usePathname();
	const url = getBaseUrl();
	const isTriggering = useRef(false);
	const isInitialLoad = useRef(true);

	const getPageType = useCallback(() => {
		if (pathname === "/") {
			return "home_page";
		}
		if (pathname.includes("/product/")) {
			return "product_detail_page";
		}
		if (pathname.includes("/products/")) {
			return "“product_listing_page”";
		}
		if (pathname.includes("checkout") && pathname.includes("summary")) {
			return "order_confirmation";
		}
		if (pathname.includes("checkout")) {
			return "checkout";
		}

		return "other";
	}, [pathname]);

	useEffect(() => {
		if (isTriggering.current || !location?.name) {
			return;
		}

		isTriggering.current = true;

		const triggerType = isInitialLoad.current ? "hard" : "soft";
		isInitialLoad.current = false;

		gtm.pageView({
			event: "page_impression",
			page_path: pathname,
			page_info: {
				url:
					typeof window !== "undefined"
						? window.location.href
						: `${url}${pathname}`,
				virtal_url: `${url}${pathname}`,
				page_path: `${pathname}`,
				page_title: typeof window !== "undefined" ? document.title : "",
				page_type: getPageType(),
				page_referrer: typeof window !== "undefined" ? document.referrer : "",
				trigger_type: triggerType,
				market: location?.name,
				locale: "nb",
			},
			user_data: getUserData().user_data,
		});

		isTriggering.current = false;
	}, [getPageType, location?.name, pathname, url]);

	return null;
};

export const GTMPurchase = ({ order }: { order: PaymentResult["order"] }) => {
	const hasRan = useRef(false);

	useEffect(() => {
		if (!order || hasRan.current || !order.currency || !order.items) return;

		gtm.purchase({
			coupon: order.discounts?.vouchers?.map((v) => v.voucher)?.join(";"),
			currency: order.currency,
			items: selectionItemsToGTMProducts({
				items: order.items,
				currency: order.currency,
			}),
			transaction_id: order.order as string,
			tax: order.totals.grandTotalPriceTaxAsNumber || 0,
			value: order.totals.grandTotalPriceAsNumber || 0,
			shipping_tier: order.shippingMethodName as string,
			shipping: order.totals.shippingPriceAsNumber || 0,
			payment_type: order.paymentMethodName as string,
			value_ink_vat: order.totals.grandTotalPriceAsNumber || 0,
			value_ex_vat:
				(order.totals.grandTotalPriceAsNumber || 0) -
				(order.totals.grandTotalPriceTaxAsNumber || 0),
		});

		hasRan.current = true;
	}, [order]);

	return null;
};
